import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
  Tabs,
  Tab,
} from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import {
  canShowAdmin,
  getCategorySession,
  getCategorySuggestions,
  reviewCategorySuggestion,
} from "services/categories";

import CategoryOutlined from "@mui/icons-material/CategoryOutlined";
import ContractAdmin from "./ContractAdmin";
import CoverageSettingsAdmin from "./CoverageSettingsAdmin";
import BookFilesAdmin from "./BookFilesAdmin";
import UsersAdmin from "./UsersAdmin";
import { ADMIN_TOOLS, hasTool } from "shared/accessPermissions";
import "./admin.css";
import ProductionAdmin from "./ProductionAdmin";
import TrayConnectionAdmin from "./TrayConnectionAdmin";
import { useSearchParams } from "react-router-dom";

const statuses = { pending: "Pendentes", approved: "Aprovadas", rejected: "Recusadas" };

export default function Admin({ user }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const availableTools = Object.keys(ADMIN_TOOLS).filter((key) => hasTool(user?.access, key));
  const requestedSection = searchParams.get("section");
  const section = availableTools.includes(requestedSection) ? requestedSection : availableTools[0];
  const [status, setStatus] = useState("pending");
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [review, setReview] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [revision, setRevision] = useState(0);
  const allowed = canShowAdmin(user);

  useEffect(() => {
    if (!allowed || section !== "categories") {
      setLoading(false);
      return undefined;
    }
    let active = true;
    setLoading(true);
    setError("");
    (async () => {
      try {
        const session = await getCategorySession();
        if (!session.isAdmin) throw new Error("Acesso restrito aos administradores.");
        const result = await getCategorySuggestions(status, page);
        if (active) setItems(result.suggestions);
      } catch (failure) {
        if (active) {
          setError(failure.message);
          setItems([]);
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [allowed, user?.uid, section, status, page, revision]);

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      await reviewCategorySuggestion(review.item.id, review.decision, note);
      setSuccess(
        review.decision === "approved"
          ? "Categoria aprovada e disponível para os autores."
          : "Sugestão recusada."
      );
      setReview(null);
      setRevision((v) => v + 1);
    } catch (failure) {
      setError(failure.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <DashboardLayout>
      <Box className="pso-admin" py={3}>
        <Typography variant="h4" mb={1} className="admin-page-title">
          Administração
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Gerencie pedidos, acompanhe a produção e mantenha os documentos da plataforma.
        </Typography>
        {!allowed ? (
          <Alert severity="error">
            Acesso restrito aos administradores com e-mail verificado. Se esta é sua conta
            administrativa, verifique o e-mail no Firebase e entre novamente.
          </Alert>
        ) : (
          <>
            <Tabs
              className="admin-navigation"
              value={section}
              onChange={(_, value) => setSearchParams({ section: value })}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ mb: 3 }}
            >
              {availableTools.map((key) => (
                <Tab key={key} label={ADMIN_TOOLS[key]} value={key} />
              ))}
            </Tabs>
            {section === "users" && <UsersAdmin />}
            {section === "files" && <BookFilesAdmin />}
            {section === "coverage" && <CoverageSettingsAdmin />}
            {section === "contract" && <ContractAdmin />}
            {section === "production" && <ProductionAdmin />}
            {section === "tray" && <TrayConnectionAdmin />}
            <Box
              className="admin-category-panel"
              sx={{ display: section === "categories" ? "block" : "none" }}
            >
              <Typography variant="h5" mb={1}>
                Sugestões de categorias
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Analise as sugestões dos autores e organize as categorias disponíveis para
                publicação.
              </Typography>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
                  {success}
                </Alert>
              )}
              <Stack
                className="category-filters"
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                mb={3}
              >
                <TextField
                  select
                  label="Situação"
                  value={status}
                  onChange={(e) => {
                    setStatus(e.target.value);
                    setPage(1);
                  }}
                  sx={{ minWidth: 200 }}
                >
                  {Object.entries(statuses).map(([key, label]) => (
                    <MenuItem key={key} value={key}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
                <Button onClick={() => setRevision((v) => v + 1)} disabled={loading}>
                  Atualizar
                </Button>
              </Stack>
              {loading ? (
                <CircularProgress aria-label="Carregando sugestões" />
              ) : items.length === 0 && !error ? (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 7,
                    px: 2,
                    border: "1px dashed #d4e2d8",
                    borderRadius: 3,
                    bgcolor: "#f9fcfa",
                  }}
                >
                  <CategoryOutlined sx={{ fontSize: 42, color: "#659a78", mb: 2 }} />
                  <Typography variant="h6">
                    Nenhuma sugestão {status === "pending" ? "pendente" : "encontrada"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    As sugestões dos autores aparecerão aqui para análise. Você pode consultar outra
                    situação no filtro acima.
                  </Typography>
                </Box>
              ) : null}
              {!loading &&
                items.map((item) => (
                  <Card key={item.id} sx={{ p: 3, mb: 2 }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      gap={2}
                      alignItems="center"
                    >
                      <Typography variant="h6">{item.name}</Typography>
                      <Chip
                        size="small"
                        label={statuses[item.status]}
                        color={item.status === "approved" ? "success" : "default"}
                      />
                    </Stack>
                    <Typography variant="body2" mt={1}>
                      Categoria principal: {item.mainCategory}
                    </Typography>
                    <Typography variant="body2">Autor: {item.authorEmail}</Typography>
                    {item.bookTitle && (
                      <Typography variant="body2">Livro: {item.bookTitle}</Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      Enviada em {new Date(item.createdAt).toLocaleString("pt-BR")}
                    </Typography>
                    {item.justification && (
                      <Typography
                        variant="body2"
                        mt={2}
                        sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
                      >
                        {item.justification}
                      </Typography>
                    )}
                    {item.reviewedBy && (
                      <Typography variant="body2" mt={2}>
                        Analisada por {item.reviewedBy}
                        {item.reviewNote ? `: ${item.reviewNote}` : ""}
                      </Typography>
                    )}
                    {item.status === "pending" && (
                      <Stack direction="row" spacing={2} mt={2}>
                        <Button
                          variant="contained"
                          color="info"
                          onClick={() => {
                            setNote("");
                            setReview({ item, decision: "approved" });
                          }}
                        >
                          Aprovar
                        </Button>
                        <Button
                          color="error"
                          onClick={() => {
                            setNote("");
                            setReview({ item, decision: "rejected" });
                          }}
                        >
                          Recusar
                        </Button>
                      </Stack>
                    )}
                  </Card>
                ))}
              <Stack direction="row" spacing={2} mt={3} alignItems="center">
                <Button disabled={page === 1 || loading} onClick={() => setPage((v) => v - 1)}>
                  Anterior
                </Button>
                <Typography variant="body2">Página {page}</Typography>
                <Button
                  disabled={items.length < 50 || loading}
                  onClick={() => setPage((v) => v + 1)}
                >
                  Próxima
                </Button>
              </Stack>
            </Box>
          </>
        )}
      </Box>
      <Dialog
        open={Boolean(review)}
        onClose={() => {
          if (!busy) setReview(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {review?.decision === "approved" ? "Aprovar categoria" : "Recusar sugestão"}
        </DialogTitle>
        <DialogContent>
          <Typography mb={2}>{review?.item.name}</Typography>
          <TextField
            label="Observação (opcional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            inputProps={{ maxLength: 1000 }}
            disabled={busy}
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setReview(null)}>
            Cancelar
          </Button>
          <Button
            disabled={busy}
            variant="contained"
            color={review?.decision === "rejected" ? "error" : "info"}
            onClick={submit}
          >
            {busy ? "Salvando..." : "Confirmar"}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
